/**
 * ISO 639-2/3 → ISO 639-1 lookup.
 * Covers both terminological (639-2/T) and bibliographic (639-2/B) variants
 * for languages commonly found in Tolgee projects and translation systems.
 *
 * These codes are NOT valid BCP 47 and won't parse via Intl.Locale,
 * so we handle them with an explicit table.
 */
const ISO3_TO_ISO1: Readonly<Record<string, string>> = {
  // Romance
  fra: "fr", fre: "fr",
  spa: "es",
  ita: "it",
  por: "pt",
  cat: "ca",
  ron: "ro", rum: "ro",
  // Germanic
  eng: "en",
  deu: "de", ger: "de",
  nld: "nl", dut: "nl",
  swe: "sv",
  dan: "da",
  nor: "no", nob: "no", nno: "no",
  isl: "is",
  afr: "af",
  // Slavic
  pol: "pl",
  ces: "cs", cze: "cs",
  slk: "sk", slo: "sk",
  bul: "bg",
  hrv: "hr",
  slv: "sl",
  srp: "sr",
  rus: "ru",
  ukr: "uk",
  bel: "be",
  mkd: "mk",
  // Finno-Ugric
  fin: "fi",
  hun: "hu",
  est: "et",
  // Baltic
  lav: "lv",
  lit: "lt",
  // Greek
  ell: "el", gre: "el",
  // Semitic
  ara: "ar",
  heb: "he",
  mlt: "mt",
  // Indo-Iranian
  hin: "hi",
  urd: "ur",
  fas: "fa", per: "fa",
  ben: "bn",
  // East Asian
  zho: "zh", chi: "zh",
  jpn: "ja",
  kor: "ko",
  // Southeast Asian
  vie: "vi",
  tha: "th",
  ind: "id",
  msa: "ms", may: "ms",
  // Others
  tur: "tr",
  aze: "az",
  kaz: "kk",
  uzb: "uz",
  mon: "mn",
  swa: "sw",
  amh: "am",
};

/**
 * Extended locale codes that carry meaningful script/region distinctions.
 * These must be preserved rather than collapsed to their base language.
 */
const EXTENDED_LOCALES: Readonly<Record<string, string>> = {
  // Chinese variants — differentiated by script
  "zh-hans":   "zh-cn",
  "zh-hant":   "zh-tw",
  "zh-cn":     "zh-cn",
  "zh-tw":     "zh-tw",
  "zh-sg":     "zh-cn",  // Singapore uses Simplified
  "zh-hk":     "zh-tw",  // HK uses Traditional
  "zh-mo":     "zh-tw",  // Macao uses Traditional
  // Portuguese variants
  "pt-br":     "pt-br",
  "pt-pt":     "pt",
  // Serbian (Latin vs Cyrillic)
  "sr-latn":   "sr",
  "sr-cyrl":   "sr",
  // Swedish
  "sv-se":     "sv",
  "swe":       "sv",
};

/**
 * Resolve BCP 47 `Intl.Locale` object to our canonical code.
 * Handles script-based disambiguation (zh-Hans → zh-cn) and
 * region-based disambiguation (pt-BR → pt-br).
 */
function resolveFromIntlLocale(locale: Intl.Locale): string {
  const lang = locale.language.toLowerCase();
  const region = locale.region?.toLowerCase();
  const script = locale.script?.toLowerCase();

  // Chinese: must keep zh-cn / zh-tw distinction
  if (lang === "zh") {
    if (script === "hans" || region === "cn" || region === "sg") return "zh-cn";
    if (script === "hant" || region === "tw" || region === "hk" || region === "mo") return "zh-tw";
    return "zh-cn"; // default to Simplified
  }

  // Portuguese: keep pt-br
  if (lang === "pt" && region === "br") return "pt-br";

  return lang;
}

/**
 * Normalise any language code variant to a canonical form compatible with
 * Baback's translation files.
 *
 * Canonical output:
 *  - ISO 639-1 lowercase for most languages:          "fr", "en", "de"
 *  - Extended lowercase for script/region variants:   "zh-cn", "zh-tw", "pt-br"
 *
 * Handles all of:
 *  - BCP 47 locale codes:   "fr-FR", "en-US", "zh-Hans-CN", "pt-BR"
 *  - Underscore separator:  "fr_FR", "zh_Hans_CN"
 *  - ISO 639-2/T codes:     "fra", "deu", "ces"
 *  - ISO 639-2/B codes:     "fre", "ger", "cze"
 *  - Case variants:         "FR", "FR-FR", "EN-US"
 *  - Already canonical:     "fr", "zh-cn"
 *
 * Returns the original code lowercased if no mapping can be found.
 */
export function normalizeLanguageCode(code: string): string {
  if (!code || typeof code !== "string") return code;

  // Normalise separators and case
  const cleaned = code.trim().replace(/_/g, "-").toLowerCase();

  // Already an extended locale we know
  if (cleaned in EXTENDED_LOCALES) return EXTENDED_LOCALES[cleaned];

  // ISO 639-2/3 (3-char, no hyphen) — not valid BCP 47, handle first
  if (/^[a-z]{3}$/.test(cleaned)) {
    return ISO3_TO_ISO1[cleaned] ?? cleaned;
  }

  // BCP 47: parse via Intl.Locale for full correctness
  try {
    const locale = new Intl.Locale(cleaned);
    return resolveFromIntlLocale(locale);
  } catch {
    // Not a valid BCP 47 tag — best-effort: take first segment
    return cleaned.split("-")[0];
  }
}

/**
 * Check whether `code` is a known, supported language code after normalisation.
 * Pass the list of supported codes from your languages manifest.
 */
export function isSupportedLanguage(code: string, supportedLngs: string[]): boolean {
  return supportedLngs.includes(normalizeLanguageCode(code));
}

/**
 * Build an i18next-compatible fallback map from a list of supported language codes.
 *
 * Rules:
 *  1. Extended locales (e.g. "pt-br") first try their base language ("pt") if
 *     it exists in supportedLngs, then fall back to ultimateFallback.
 *  2. Base languages fall back directly to ultimateFallback.
 *  3. zh-cn / zh-tw do NOT fall back to each other (different scripts).
 *  4. A "default" key covers any language not explicitly listed.
 *
 * Example output for supportedLngs = ["fr","en","pt","pt-br","zh-cn","zh-tw"]:
 * {
 *   "pt-br": ["pt", "en"],
 *   "zh-cn": ["en"],
 *   "zh-tw": ["en"],
 *   "fr":    ["en"],
 *   "pt":    ["en"],
 *   "default": ["en"],
 * }
 *
 * Pass the result directly to i18next `fallbackLng` option.
 */
/**
 * Maps canonical codes used in Baback's translation files to valid BCP 47
 * tags for use with Intl APIs (DateTimeFormat, Collator, etc.).
 * Pass i18n.language through this before any Intl call.
 */
const CANONICAL_TO_BCP47: Readonly<Record<string, string>> = {};

export function toIntlLocale(canonicalCode: string): string {
  return CANONICAL_TO_BCP47[canonicalCode] ?? canonicalCode;
}

export function buildFallbackMap(
  supportedLngs: string[],
  ultimateFallback = "en",
): Record<string, string[]> {
  const supported = new Set(supportedLngs);
  const map: Record<string, string[]> = {};

  for (const lng of supportedLngs) {
    if (lng === ultimateFallback) continue;

    const chain: string[] = [];

    // For extended locales (e.g. "pt-br", "zh-cn"), check if base exists
    if (lng.includes("-")) {
      const base = lng.split("-")[0];
      // Add base only if it's supported AND it's not the same script group
      // (zh-cn and zh-tw share base "zh" but shouldn't cross-fallback)
      if (supported.has(base) && base !== "zh") {
        chain.push(base);
      }
    }

    chain.push(ultimateFallback);
    map[lng] = chain;
  }

  map["default"] = [ultimateFallback];
  return map;
}
